import clsx from 'clsx';
import { FieldValuesType, GetLabel, HabitationForm, MaritalStatus, ValidationStateType } from '../types';
import { Field } from '@/isomorphic/components/forms/components/field';
import { validators } from '@/hooks/use-validation';
import { useLocalization } from '@/hooks/use-localization';
import { Checkbox } from '@/isomorphic/components/forms/components/checkbox';
import { useMemo } from 'react';
import { useEmploymentFields } from '../hooks/use-employment-fields';
import { YearMonthSelect } from './year-month-select';

type Props = {
    fields: FieldValuesType;
    updateFields: (value: Partial<FieldValuesType>) => void;
    getLabel: GetLabel;
    brandColor: string;
    headingTextColor: 'r-text-black' | 'r-text-primary-500';
    showValidationErrors: boolean;
    validationState: ValidationStateType;
    maritalOptions: MaritalStatus[];
    habitationOptions: HabitationForm[];
    childrenOptions: {
        value: string;
        label: string;
    }[];
};

export const CoapplicantFields = ({
    headingTextColor,
    brandColor,
    getLabel,
    fields,
    showValidationErrors,
    validationState,
    updateFields,
    maritalOptions,
    habitationOptions,
}: Props) => {
    const { getLocale, getLanguage } = useLocalization();
    const employmentType = useMemo(() => fields.coapplicantEmploymentType, [fields.coapplicantEmploymentType]);
    const { active, options: employmentOptions } = useEmploymentFields(employmentType, true);
    const validateSsn = (value: string) => validators.socialSecurityNumber(value, getLanguage());

    return (
        <>
            <div>
                <Field
                    required
                    grouped="top"
                    validation={value => validateSsn(value)}
                    value={fields.coapplicantGovernmentId || ''}
                    onChange={value => updateFields({ coapplicantGovernmentId: value })}
                    label={getLabel('governmentIdPlaceholder')}
                    showRequiredError={showValidationErrors && !validationState.coapplicantGovernmentId}
                />
                <Field
                    required
                    grouped="middle"
                    value={fields.coapplicantMobileNumber || ''}
                    label={getLabel('phoneMobile')}
                    infoText={getLabel('coapplicantPhoneMobileInfoText')}
                    onChange={value => updateFields({ coapplicantMobileNumber: value })}
                    validation={v => validators.phoneMobile(v, getLocale())}
                    showRequiredError={showValidationErrors && !validationState.coapplicantMobileNumber}
                />
                <Field
                    required
                    grouped="bottom"
                    value={fields.coapplicantEmailAddress || ''}
                    validation={validators.email}
                    label={getLabel('contactEmail')}
                    infoText={getLabel('coapplicantContactEmailInfoText')}
                    onChange={value => updateFields({ coapplicantEmailAddress: value })}
                    showRequiredError={showValidationErrors && !validationState.coapplicantEmailAddress}
                />
            </div>
            <div>
                <h4 className={clsx('r-mb-3 r-text-xl r-font-semibold', headingTextColor)}>{getLabel('livingSituation')}</h4>
                <fieldset className="r-m-0 r-mb-6 r-flex r-flex-col r-gap-2 r-border-none r-p-0">
                    <legend>{`${getLabel('applicantMaritalStatus')} *`}</legend>
                    {maritalOptions.map(option => (
                        <Checkbox
                            key={option}
                            type="radio"
                            name="co-applicant-marital-option"
                            brandColor={brandColor}
                            label={getLabel(option)}
                            checked={fields.coapplicantMaritalStatus === option}
                            onChange={checked => checked && updateFields({ coapplicantMaritalStatus: option })}
                            showRequiredError={showValidationErrors && !validationState.coapplicantMaritalStatus}
                        />
                    ))}
                </fieldset>
                <fieldset className="r-m-0 r-flex r-flex-col r-gap-2 r-border-none r-p-0">
                    <legend>{`${getLabel('applicantHabitationForm')} *`}</legend>
                    {habitationOptions.map(option => (
                        <Checkbox
                            key={option}
                            type="radio"
                            brandColor={brandColor}
                            label={getLabel(option)}
                            name="co-habitation-option"
                            checked={fields.coapplicantHabitationForm === option}
                            onChange={checked => checked && updateFields({ coapplicantHabitationForm: option })}
                            showRequiredError={showValidationErrors && !validationState.coapplicantHabitationForm}
                        />
                    ))}
                </fieldset>
            </div>
            <div className="r-space-y-4">
                <h4 className={clsx('r-mb-3 r-text-xl r-font-semibold', headingTextColor)}>{getLabel('employment')}</h4>
                <fieldset className="r-m-0 r-mb-10 r-flex r-flex-col r-gap-2 r-border-none r-p-0">
                    <legend>{`${getLabel('employmentType')} *`}</legend>
                    {employmentOptions.map(option => (
                        <Checkbox
                            type="radio"
                            key={option.value}
                            brandColor={brandColor}
                            name="co-employment-option"
                            label={getLabel(option.value)}
                            checked={fields.coapplicantEmploymentType === option.value}
                            showRequiredError={showValidationErrors && !validationState.coapplicantEmploymentType}
                            onChange={checked => checked && updateFields({ coapplicantEmploymentType: option.value })}
                        />
                    ))}
                </fieldset>
                {employmentType && (
                    <>
                        {active.coapplicantEmploymentTypeQualifying && (
                            <fieldset className="r-m-0 r-border-none r-p-0 r-py-2">
                                <legend className="r-mb-1">{getLabel('coapplicantEmploymentTypeQualifyingText')} *</legend>

                                <div className="r-flex r-gap-4">
                                    <Checkbox
                                        type="radio"
                                        value="true"
                                        brandColor={brandColor}
                                        name="coapplicantEmploymentTypeQualifying"
                                        label={getLabel('yes')}
                                        checked={fields.coapplicantEmploymentTypeQualifying === 'true'}
                                        onChange={() => updateFields({ coapplicantEmploymentTypeQualifying: 'true' })}
                                        showRequiredError={showValidationErrors && !validationState.coapplicantEmploymentTypeQualifying}
                                    />

                                    <Checkbox
                                        type="radio"
                                        value="false"
                                        brandColor={brandColor}
                                        name="coapplicantEmploymentTypeQualifying"
                                        label={getLabel('no')}
                                        checked={fields.coapplicantEmploymentTypeQualifying === 'false'}
                                        onChange={() => updateFields({ coapplicantEmploymentTypeQualifying: 'false' })}
                                        showRequiredError={showValidationErrors && !validationState.coapplicantEmploymentTypeQualifying}
                                    />
                                </div>
                            </fieldset>
                        )}
                        {active.coapplicantEmployer && (
                            <div className="r-flex r-flex-col r-gap-3">
                                <Field
                                    required
                                    value={fields.coapplicantEmployer ?? ''}
                                    label={`${getLabel('applicantEmployer')}`}
                                    infoText={getLabel('coapplicantEmployerInfoText')}
                                    onChange={value => updateFields({ coapplicantEmployer: value })}
                                    showRequiredError={showValidationErrors && !validationState.coapplicantEmployer}
                                />
                            </div>
                        )}
                        {active.coapplicantEmploymentYearFrom && (
                            <div className="r-flex r-flex-col r-gap-3">
                                <YearMonthSelect
                                    fields={fields}
                                    updateFields={updateFields}
                                    labels={{
                                        title: getLabel('employmentYearFromTitle'),
                                        year: getLabel('employmentYearPlaceholder'),
                                        month: getLabel('employmentMonthPlaceholder'),
                                        infoTextYear: getLabel('coapplicantEmploymentYearFromInfoText'),
                                        infoTextMonth: getLabel('coapplicantEmploymentMonthFromInfoText'),
                                    }}
                                    showRequiredError={
                                        showValidationErrors &&
                                        (!validationState.coapplicantEmploymentYearFrom || !validationState.coapplicantEmploymentMonthFrom)
                                    }
                                    coapplicantField
                                />
                            </div>
                        )}
                        {active.coapplicantEmploymentYearUntil && (
                            <div className="r-flex r-flex-col r-gap-3">
                                <YearMonthSelect
                                    future
                                    fields={fields}
                                    updateFields={updateFields}
                                    labels={{
                                        title: getLabel('employmentYearUntilTitle'),
                                        year: getLabel('employmentYearPlaceholder'),
                                        month: getLabel('employmentMonthPlaceholder'),
                                        infoTextYear: getLabel('coapplicantEmploymentYearUntilInfoText'),
                                        infoTextMonth: getLabel('coapplicantEmploymentMonthUntilInfoText'),
                                    }}
                                    showRequiredError={
                                        showValidationErrors &&
                                        (!validationState.coapplicantEmploymentYearUntil || !validationState.coapplicantEmploymentMonthUntil)
                                    }
                                    coapplicantField={true}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
